import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { appConfig } from '../config/app';

const useNotifications = () => {
  const { state, actions } = useApp();
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const socketRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const isReconnecting = useRef(false); // Add flag to prevent concurrent reconnections

  // Get the WebSocket URL
  const getSocketUrl = () => {
    const apiUrl = appConfig.api.baseURL;
    const baseUrl = apiUrl.replace('/api', '');
    return baseUrl;
  };

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!state.isAuthenticated || !state.user) {
      console.log('User not authenticated, skipping WebSocket connection');
      return;
    }

    // Prevent duplicate connections
    if (socketRef.current && socketRef.current.connected) {
      console.log('WebSocket already connected, skipping duplicate connection');
      return;
    }

    // Clean up existing connection before creating new one
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    try {
      setConnectionStatus('connecting');
      console.log('🔌 Connecting to WebSocket server...');

      const socketUrl = getSocketUrl();
      console.log('WebSocket URL:', socketUrl);

      // Get user info for connection
      const userId = state.user.mobile || state.user.mobileNo || 'unknown';
      const userName = state.user.name || state.user.f_name || 'User';
      const userRole = state.user.role || 'user';

      console.log('🔍 Connecting with user info:', { userId, userName, userRole });

      const newSocket = io(socketUrl, {
        query: {
          userId: userId,
          userName: userName,
          userRole: userRole
        },
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true
      });

      newSocket.on('connect', () => {
        console.log('✅ WebSocket connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0;
        isReconnecting.current = false; // Reset reconnection flag on successful connection
        
        // actions.showSnackbar(
        //   `Connected to notifications (${state.user.name})`,
        //   'success'
        // );
      });

      newSocket.on('disconnect', (reason) => {
        console.log('❌ WebSocket disconnected:', reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        if (reason === 'io server disconnect') {
          // Server disconnected, try to reconnect
          handleReconnect();
        }
      });

      newSocket.on('connect_error', (error) => {
        console.error('💥 WebSocket connection error:', error.message);
        setIsConnected(false);
        setConnectionStatus('error');
        
        // Show error message to user
        actions.showSnackbar(
          'Failed to connect to notifications',
          'error'
        );
        
        handleReconnect();
      });

      newSocket.on('notification', (notification) => {
        console.log('📢 Received notification:', notification);
        
        // Add to notifications queue
        setNotifications(prev => [...prev, notification]);
        
        // Show snackbar for quick feedback
        actions.showSnackbar(
          `New notification: ${notification.title}`,
          notification.type || 'info'
        );

        // Send acknowledgment
        newSocket.emit('notification_ack', {
          notificationId: notification.id,
          timestamp: new Date().toISOString()
        });
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionStatus('error');
      actions.showSnackbar('Failed to initialize notifications', 'error');
    }
  }, [state.isAuthenticated, state.user, actions]);

  // Reconnect logic
  const handleReconnect = useCallback(() => {
    if (isReconnecting.current) {
      console.log('🔄 Already reconnecting, skipping duplicate attempt');
      return;
    }

    if (reconnectAttempts.current < maxReconnectAttempts) {
      isReconnecting.current = true;
      reconnectAttempts.current++;
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
      
      console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
      setConnectionStatus('reconnecting');
      
      setTimeout(() => {
        isReconnecting.current = false;
        connect();
      }, delay);
    } else {
      console.log('❌ Max reconnection attempts reached');
      setConnectionStatus('failed');
      actions.showSnackbar('Unable to reconnect to notifications', 'error');
      isReconnecting.current = false;
    }
  }, [connect, actions]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('🔌 Disconnecting from WebSocket server...');
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
      setConnectionStatus('disconnected');
    }
  }, []);

  // Manually reconnect
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttempts.current = 0;
    setTimeout(connect, 1000);
  }, [disconnect, connect]);

  // Handle notification dismissal
  const dismissNotification = useCallback((notification) => {
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    
    // Send dismissal event to server
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('notification_dismiss', {
        notificationId: notification.id,
        timestamp: new Date().toISOString()
      });
    }
    
    console.log('🗑️ Notification dismissed:', notification.id);
  }, []);

  // Handle notification view (with optional action for order notifications)
  const viewNotification = useCallback((notification, action = null) => {
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    
    // Send view event to server
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('notification_view', {
        notificationId: notification.id,
        actionType: action?.type || 'view',
        timestamp: new Date().toISOString()
      });
    }
    
    // Handle order notification actions
    if (notification.type === 'order_notification' && action) {
      if (action.type === 'view_order') {
        // Trigger order modal opening
        handleOrderView(notification);
      }
    }
    
    console.log('👁️ Notification viewed:', notification.id, action ? `with action: ${action.type}` : '');
  }, []);

  // Handle order view action
  const handleOrderView = useCallback((notification) => {
    // Navigate to orders page when view order is clicked
    if (notification.orderId) {
      navigate('/admin/orders');
      actions.showSnackbar(`Navigating to orders page for order #${notification.orderId}`, 'info');
    }
  }, [navigate, actions]);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    console.log('🧹 All notifications cleared');
  }, []);

  // Auto-connect when user is authenticated
  useEffect(() => {
    if (state.isAuthenticated && state.user) {
      connect();
    } else {
      disconnect();
      setNotifications([]);
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [state.isAuthenticated, state.user]); // Removed connect and disconnect from dependencies

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('📱 Tab hidden, maintaining connection');
      } else {
        console.log('📱 Tab visible, checking connection');
        if (state.isAuthenticated && !isConnected) {
          reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isAuthenticated, isConnected, reconnect]);

  return {
    socket,
    isConnected,
    connectionStatus,
    notifications,
    connect,
    disconnect,
    reconnect,
    dismissNotification,
    viewNotification,
    clearAllNotifications,
    // Helper functions
    hasNotifications: notifications.length > 0,
    notificationCount: notifications.length,
    // Connection status checks
    isConnecting: connectionStatus === 'connecting',
    isReconnecting: connectionStatus === 'reconnecting',
    hasConnectionError: connectionStatus === 'error' || connectionStatus === 'failed'
  };
};

export default useNotifications; 